import React, {
  ReactNode,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react';

interface AudioPlayerContextType {
  loading: boolean;
  playing: boolean;
  position: number;
  currentTime: number;
  endTime: number;
  player: React.MutableRefObject<HTMLAudioElement | null>;
  initializeAudioPlayer: () => void;
  unmountAudioPlayer: () => void;
  skipTo: (seconds: number) => void;
  secondsToTime: (seconds: number) => string;
  togglePlayPause: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(
  undefined,
);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const player = useRef<HTMLAudioElement | null>(null);

  const initializeAudioPlayer = () => {
    if (player && player.current) {
      const makePlaying = () => setPlaying(true);
      const makeNotPlaying = () => setPlaying(false);

      player.current.addEventListener('canplaythrough', () => {
        setLoading(false);
        setEndTime(player.current!.duration);
      });
      player.current.addEventListener('playing', makePlaying);
      player.current.addEventListener('timeupdate', () => {
        setCurrentTime(player.current!.currentTime);
        setPosition(
          (player.current!.currentTime / player.current!.duration) * 100,
        );
      });
      player.current.addEventListener('pause', makeNotPlaying);
      player.current.addEventListener('ended', makeNotPlaying);
      player.current.addEventListener('error', () => {
        makeNotPlaying();
      });
    }
  };

  const unmountAudioPlayer = () => {
    if (player && player.current) {
      const makePlaying = () => setPlaying(true);
      const makeNotPlaying = () => setPlaying(false);

      player.current.removeEventListener('canplaythrough', () => {
        setLoading(false);
        setEndTime(player.current!.duration);
      });
      player.current.removeEventListener('playing', makePlaying);
      player.current.removeEventListener('timeupdate', () => {
        setCurrentTime(player.current!.currentTime);
        setPosition(
          (player.current!.currentTime / player.current!.duration) * 100,
        );
      });
      player.current.removeEventListener('pause', makeNotPlaying);
      player.current.removeEventListener('ended', makeNotPlaying);
      player.current.removeEventListener('error', () => {
        makeNotPlaying();
      });
    }
  };

  function skipTo(seconds: number) {
    if (player && player.current) {
      let currentPosition = 0;
      if (seconds <= player.current.duration) {
        currentPosition = (10 / player.current.duration) * 100;
      }

      player.current.pause();
      setCurrentTime(seconds);
      setPosition(currentPosition);

      if (player.current.fastSeek instanceof Function) {
        player.current.fastSeek(seconds);
      } else {
        player.current.currentTime = seconds;
      }

      player.current.play();
    }
  }

  function toTimeString(time: number) {
    const date = new Date();
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(time);

    return date.toTimeString().slice(3, 8);
  }

  function secondsToTime(seconds: number) {
    const defaultTimeStr = '00:00';
    const invalidTimeStr = '--:--';

    const timeStr = Number.isNaN(seconds)
      ? invalidTimeStr
      : toTimeString(seconds);

    return loading ? defaultTimeStr : timeStr;
  }

  function togglePlayPause() {
    if (player && player.current) {
      if (playing) {
        player.current.pause();
      } else {
        player.current.play();
      }
    }
  }

  return (
    <AudioPlayerContext.Provider
      value={{
        loading,
        playing,
        position,
        currentTime,
        endTime,
        player,
        initializeAudioPlayer,
        unmountAudioPlayer,
        skipTo,
        secondsToTime,
        togglePlayPause,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);

  if (!context) {
    throw new Error('useAudioPlayer must be used within a AudioPlayerProvider');
  }

  return context;
}
